import React, { useState, useRef, useEffect } from "react";

const CustomSelect = ({
  id,
  name,
  value,
  options = [],
  onChange,
  placeholder = "Select",
  hasError,
  className = "",
  children,
  disabled = false,
  searchable = false,
  searchPlaceholder = "Search...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Auto focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchable]);

  let optionList = [];

  // Support options prop
  if (options.length > 0) {
    optionList = options.map((item) => {
      if (typeof item === "object" && item !== null) {
        return {
          label: item.label ?? item.country_name ?? item.name ?? String(item.value ?? ""),
          value: item.value ?? item.country_name ?? item.name ?? "",
        };
      }
      return {
        label: String(item),
        value: item,
      };
    });
  }
  // Support <option> children
  else if (children) {
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === "option") {
        optionList.push({
          value: child.props.value,
          label: child.props.children,
        });
      }
    });
  }

  // Filter options based on search query
  const filteredList = searchable && searchQuery.trim()
    ? optionList.filter((item) =>
      String(item.label || item.value || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase().trim())
    )
    : optionList;

  const selectedOption = optionList.find(
    (item) => String(item.value) === String(value)
  );

  const selectedLabel = selectedOption ? selectedOption.label : placeholder;

  return (
    <div
      className="custom-select-wrapper"
      ref={dropdownRef}
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      <div
        id={id}
        className={`form-select ${className} ${hasError ? "is-invalid" : ""}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (isOpen) setSearchQuery("");
          }
        }}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#eff2f7" : "#ffffff",
          border: hasError ? "1px solid #f06548" : "1px solid #ced4da",
          borderRadius: "6px",
          opacity: disabled ? 0.7 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "38px",
          userSelect: "none",
          padding: "0.375rem 0.75rem",
          backgroundImage: "none",
          transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
          boxShadow: isOpen && !hasError ? "0 0 0 0.2rem rgba(85, 110, 230, 0.2)" : "none",
          borderColor: isOpen && !hasError ? "#86b7fe" : (hasError ? "#f06548" : "#ced4da"),
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "13px",
            fontWeight: selectedOption ? "500" : "400",
            color: selectedOption ? "#212529" : "#878a99",
            marginRight: "8px",
          }}
        >
          {selectedOption ? selectedLabel : placeholder}
        </span>
        <i
          className="mdi mdi-chevron-down text-muted"
          style={{
            fontSize: "16px",
            transition: "transform 0.2s ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        />
      </div>

      {!disabled && isOpen && (
        <div
          className="shadow-lg rounded-3"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "#ffffff",
            border: "1px solid #e9ebec",
            borderRadius: "8px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12)",
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          {searchable && (
            <div
              style={{
                padding: "8px 10px",
                borderBottom: "1px solid #f1f2f3",
                backgroundColor: "#ffffff",
              }}
            >
              <div className="position-relative">
                <i
                  className="bx bx-search text-muted position-absolute"
                  style={{
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "15px",
                    pointerEvents: "none",
                  }}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    paddingLeft: "32px",
                    paddingRight: searchQuery ? "28px" : "10px",
                    fontSize: "13px",
                    borderRadius: "6px",
                    borderColor: "#ced4da",
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="btn btn-link p-0 position-absolute text-muted"
                    style={{
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "16px",
                      lineHeight: 1,
                      textDecoration: "none",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchQuery("");
                      searchInputRef.current?.focus();
                    }}
                    title="Clear search"
                  >
                    <i className="bx bx-x" />
                  </button>
                )}
              </div>
            </div>
          )}

          <ul
            style={{
              maxHeight: 230,
              overflowY: "auto",
              margin: 0,
              padding: "4px 0",
              listStyle: "none",
            }}
          >
            {filteredList.length > 0 ? (
              filteredList.map((item, index) => {
                const isSelected = String(value) === String(item.value);
                return (
                  <li
                    key={index}
                    onClick={() => {
                      onChange?.({
                        target: {
                          name,
                          value: item.value,
                        },
                      });
                      setIsOpen(false);
                      setSearchQuery("");
                    }}
                    style={{
                      padding: "8px 14px",
                      cursor: "pointer",
                      background: isSelected
                        ? "rgba(85, 110, 230, 0.08)"
                        : "transparent",
                      color: isSelected ? "#556ee6" : "#212529",
                      fontWeight: isSelected ? "600" : "400",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = "#f3f6f9";
                        e.currentTarget.style.color = "#556ee6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#212529";
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    {isSelected && (
                      <i className="mdi mdi-check text-primary font-size-15" />
                    )}
                  </li>
                );
              })
            ) : (
              <li
                className="text-center text-muted py-3"
                style={{ fontSize: "13px" }}
              >
                <i className="bx bx-search font-size-18 d-block mb-1 text-muted" />
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;